import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  TrendingUp, Users, CreditCard, RefreshCw, Search, ExternalLink,
  IndianRupee, Wallet, XCircle, Undo2, Loader2,
} from "lucide-react";
import Nav from "@/components/Nav";
import FloatingShapes from "@/components/FloatingShapes";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const SYMBOLS = { INR: "₹", USD: "$", EUR: "€", GBP: "£", CAD: "C$", AUD: "A$",
  AED: "AED ", SGD: "S$", JPY: "¥", MXN: "$", SAR: "SR ", BRL: "R$", NGN: "₦" };
const PLAN_LABEL = { pro_monthly: "Pro Monthly", pro_annual: "Pro Annual", lifetime: "Lifetime", "—": "—" };
const POSTHOG_URL = "https://us.posthog.com";

function money(cur, amt) {
  const s = SYMBOLS[cur] || (cur + " ");
  return `${s}${Number(amt || 0).toLocaleString()}`;
}
// Render a {CUR: {amount}} map as "₹20,994 · $5" style string.
function moneyMap(map, key = "amount") {
  const entries = Object.entries(map || {});
  if (!entries.length) return "—";
  return entries.map(([cur, v]) => money(cur, v[key])).join("  ·  ");
}

const Card = ({ icon: Icon, label, value, sub, accent = "text-stone-900", testid }) => (
  <div className="sticky-card p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-stone-500">
      {Icon && <Icon className="w-4 h-4" />}
      <span className="text-xs uppercase tracking-wider">{label}</span>
    </div>
    <div className={`font-display text-3xl mt-2 ${accent}`}>{value}</div>
    {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [ov, setOv] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [promos, setPromos] = useState(null);
  const [search, setSearch] = useState("");
  const [loadingData, setLoadingData] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadOverview = async () => {
    try { const r = await api.get("/admin/overview"); setOv(r.data); }
    catch { /* handled by guard */ }
  };
  const loadCustomers = async (q = "") => {
    try { const r = await api.get(`/admin/customers${q ? `?search=${encodeURIComponent(q)}` : ""}`); setCustomers(r.data.customers || []); }
    catch { /* admin only */ }
  };
  const loadPromos = async () => {
    try { const r = await api.get("/admin/promos"); setPromos(r.data); }
    catch { /* admin only */ }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    (async () => { setLoadingData(true); await Promise.all([loadOverview(), loadCustomers(), loadPromos()]); setLoadingData(false); })();
    // eslint-disable-next-line
  }, [user?.is_admin]);

  const openCustomer = async (uid) => {
    setDetailLoading(true); setDetail({ loading: true });
    try { const r = await api.get(`/admin/customer/${uid}`); setDetail(r.data); }
    catch { setDetail(null); }
    finally { setDetailLoading(false); }
  };

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const funnelMax = ov?.funnel?.[0]?.users || 1;
  const grossStr = ov ? moneyMap(ov.revenue_by_currency) : "—";
  const netStr = ov ? moneyMap(ov.net_revenue_by_currency) : "—";
  const monthStr = ov ? moneyMap(ov.revenue_this_month) : "—";

  return (
    <div className="min-h-screen relative">
      <FloatingShapes />
      <Nav />
      <main className="max-w-7xl mx-auto px-6 pt-28 pb-20 relative z-10" data-testid="admin-dashboard">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-hand text-stone-400 text-xl">the control room</div>
            <h1 className="font-display text-4xl sm:text-5xl">Business dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { loadOverview(); loadCustomers(search); loadPromos(); }}
                    className="btn-ghost text-sm inline-flex items-center gap-2 border border-stone-200"
                    data-testid="admin-refresh-button">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <a href={POSTHOG_URL} target="_blank" rel="noopener noreferrer"
               className="btn-primary text-sm inline-flex items-center gap-2"
               data-testid="admin-posthog-link">
              <ExternalLink className="w-4 h-4" /> Visitor analytics (PostHog)
            </a>
          </div>
        </div>
        <p className="text-sm text-stone-500 mt-2 max-w-2xl">
          Revenue is webhook/verified server-side — the source of truth. Visits, traffic sources,
          devices &amp; countries live in your PostHog project (button above).
        </p>

        {loadingData && !ov ? (
          <div className="mt-16 flex items-center gap-3 text-stone-500" data-testid="admin-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Gathering the numbers…
          </div>
        ) : ov && (
          <>
            {/* KPI ROW */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              <Card icon={Wallet} label="Gross revenue" value={grossStr} accent="text-emerald-700"
                    sub={`Net after refunds: ${netStr}`} testid="kpi-gross-revenue" />
              <Card icon={Users} label="Paying customers" value={ov.paying_customers}
                    sub={`${ov.users.total} total users`} testid="kpi-paying-customers" />
              <Card icon={IndianRupee} label="Revenue this month" value={monthStr}
                    sub={`${ov.revenue_this_month && Object.values(ov.revenue_this_month).reduce((a, v) => a + (v.count || 0), 0)} payments`} testid="kpi-revenue-month" />
              <Card icon={CreditCard} label="Payments" value={ov.payments.paid}
                    sub={`${ov.payments.failed} failed · ${ov.payments.refunded} refunded`} testid="kpi-payments" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <Card label="New users (7d)" value={ov.users.new_7d} testid="kpi-new-7d" />
              <Card label="New users (30d)" value={ov.users.new_30d} testid="kpi-new-30d" />
              <Card label="Decks created" value={ov.content.decks} testid="kpi-decks" />
              <Card label="Quizzes generated" value={ov.content.quizzes} testid="kpi-quizzes" />
            </div>

            {/* FUNNEL + REVENUE BREAKDOWN */}
            <div className="grid lg:grid-cols-2 gap-4 mt-8">
              <div className="sticky-card p-6" data-testid="admin-funnel">
                <div className="flex items-center gap-2 text-stone-700">
                  <TrendingUp className="w-5 h-5 text-indigo-500" />
                  <span className="font-display text-2xl">Acquisition funnel</span>
                </div>
                <div className="mt-5 space-y-3">
                  {ov.funnel.map((f, i) => {
                    const pct = Math.max(3, Math.round((f.users / funnelMax) * 100));
                    const conv = i === 0 ? 100 : Math.round((f.users / funnelMax) * 100);
                    return (
                      <div key={f.step} data-testid={`funnel-step-${i}`}>
                        <div className="flex justify-between text-sm text-stone-600 mb-1">
                          <span>{f.step}</span>
                          <span className="tabular-nums">{f.users} <span className="text-stone-400">· {conv}%</span></span>
                        </div>
                        <div className="h-3 rounded-full bg-stone-100 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-indigo-400 to-indigo-500 transition-all"
                               style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="sticky-card p-6" data-testid="admin-revenue-breakdown">
                <div className="font-display text-2xl text-stone-700">Revenue breakdown</div>
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">By provider</div>
                  <div className="space-y-2">
                    {Object.entries(ov.revenue_by_provider || {}).length === 0 && <div className="text-sm text-stone-400">No revenue yet</div>}
                    {Object.entries(ov.revenue_by_provider || {}).map(([prov, curmap]) => (
                      <div key={prov} className="flex justify-between text-sm" data-testid={`rev-provider-${prov}`}>
                        <span className="capitalize text-stone-600">{prov}</span>
                        <span className="font-medium text-stone-800">{moneyMap(curmap)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">By plan</div>
                  <div className="space-y-2">
                    {Object.entries(ov.revenue_by_plan || {}).map(([plan, curmap]) => (
                      <div key={plan} className="flex justify-between text-sm" data-testid={`rev-plan-${plan}`}>
                        <span className="text-stone-600">{PLAN_LABEL[plan] || plan}</span>
                        <span className="font-medium text-stone-800">{moneyMap(curmap)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {ov.payments.refunded > 0 && (
                  <div className="mt-5 flex items-center gap-2 text-sm text-rose-600" data-testid="rev-refunds">
                    <Undo2 className="w-4 h-4" /> Refunds: {moneyMap(ov.refunds_by_currency)} ({ov.payments.refunded})
                  </div>
                )}
              </div>
            </div>

            {/* CUSTOMERS */}
            <div className="sticky-card p-6 mt-8" data-testid="admin-customers">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="font-display text-2xl text-stone-700">Customers <span className="text-stone-400 text-lg">({customers.length})</span></div>
                <div className="relative">
                  <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)}
                         onKeyDown={(e) => e.key === "Enter" && loadCustomers(search)}
                         placeholder="Search email or name…" className="pl-9 w-64"
                         data-testid="admin-customer-search" />
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-stone-400 uppercase text-xs tracking-wider border-b border-stone-100">
                      <th className="py-2 pr-4 font-medium">Customer</th>
                      <th className="py-2 px-2 font-medium">Tier</th>
                      <th className="py-2 px-2 font-medium text-right">Decks</th>
                      <th className="py-2 px-2 font-medium text-right">Quizzes</th>
                      <th className="py-2 px-2 font-medium text-right">Paywall</th>
                      <th className="py-2 pl-2 font-medium text-right">Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => (
                      <tr key={c.user_id} onClick={() => openCustomer(c.user_id)}
                          className="border-b border-stone-50 hover:bg-stone-50 cursor-pointer transition-colors"
                          data-testid={`customer-row-${c.user_id}`}>
                        <td className="py-2.5 pr-4">
                          <div className="text-stone-800">{c.email}</div>
                          {c.name && <div className="text-xs text-stone-400">{c.name}</div>}
                        </td>
                        <td className="py-2.5 px-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${c.paid ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                            {PLAN_LABEL[c.tier] || c.tier || "free"}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{c.decks}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{c.quizzes}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{c.paywall_hits}</td>
                        <td className="py-2.5 pl-2 text-right tabular-nums font-medium">
                          {c.spend ? money(c.spend.currency, c.spend.amount) : <span className="text-stone-300">—</span>}
                        </td>
                      </tr>
                    ))}
                    {customers.length === 0 && (
                      <tr><td colSpan={6} className="py-8 text-center text-stone-400">No customers found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* PROMO REDEMPTIONS */}
            <div className="sticky-card p-6 mt-8" data-testid="admin-promos">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="font-display text-2xl text-stone-700">
                  Promo redemptions <span className="text-stone-400 text-lg">({promos?.total || 0})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {promos && Object.entries(promos.by_code || {}).map(([code, n]) => (
                    <span key={code} className="text-xs px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium"
                          data-testid={`promo-code-count-${code}`}>
                      {code} · {n}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-stone-400 uppercase text-xs tracking-wider border-b border-stone-100">
                      <th className="py-2 pr-4 font-medium">User</th>
                      <th className="py-2 px-2 font-medium">Code</th>
                      <th className="py-2 px-2 font-medium text-right">Days</th>
                      <th className="py-2 px-2 font-medium">Redeemed</th>
                      <th className="py-2 px-2 font-medium">Expires</th>
                      <th className="py-2 pl-2 font-medium text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(promos?.redemptions || []).map((r, i) => (
                      <tr key={i} className="border-b border-stone-50" data-testid={`promo-row-${i}`}>
                        <td className="py-2.5 pr-4 text-stone-800">{r.email || "—"}</td>
                        <td className="py-2.5 px-2"><span className="tag-pill bg-indigo-50 text-indigo-600">{r.code}</span></td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{r.days}</td>
                        <td className="py-2.5 px-2 text-stone-500">{r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : "—"}</td>
                        <td className="py-2.5 px-2 text-stone-500">{r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"}</td>
                        <td className="py-2.5 pl-2 text-right">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                            {r.active ? "Active" : "Expired"}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {(!promos || promos.total === 0) && (
                      <tr><td colSpan={6} className="py-8 text-center text-stone-400">No promo redemptions yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      {/* CUSTOMER DETAIL */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="customer-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detailLoading || detail?.loading ? "Loading…" : detail?.user?.email}</DialogTitle>
          </DialogHeader>
          {detail && !detail.loading && (
            <div>
              <div className="flex flex-wrap gap-4 text-sm">
                <div><span className="text-stone-400">Tier</span> · <b>{PLAN_LABEL[detail.user.tier] || detail.user.tier}</b></div>
                <div><span className="text-stone-400">Auth</span> · {detail.user.auth_provider}</div>
                <div><span className="text-stone-400">Decks</span> · {detail.stats.decks}</div>
                <div><span className="text-stone-400">Quizzes</span> · {detail.stats.quizzes}</div>
                <div><span className="text-stone-400">Purchases</span> · {detail.stats.purchases}</div>
              </div>
              <div className="mt-4 text-xs uppercase tracking-wider text-stone-400">Activity timeline</div>
              <div className="mt-2 max-h-72 overflow-y-auto space-y-2 pr-1">
                {detail.timeline.length === 0 && <div className="text-sm text-stone-400">No activity yet</div>}
                {detail.timeline.map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm" data-testid={`timeline-item-${i}`}>
                    <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                      t.type === "payment" ? "bg-emerald-500" : t.type === "paywall" ? "bg-amber-500" : "bg-indigo-400"}`} />
                    <div>
                      <div className="text-stone-700">{t.label}</div>
                      <div className="text-xs text-stone-400">{t.ts ? new Date(t.ts).toLocaleString() : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(detailLoading || detail?.loading) && (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
